import type {
  EntitlementSnapshot,
  HoverIdlePaymentState,
} from "@/features/payment/types";
import type {
  HoverIdleHovCellState,
  HoverIdleSettingsState,
  HoverIdleTimCellState,
  NHovState,
  OHovState,
} from "../types";
import { defaultPayment } from "./hoverIdleDefaults";
import type { HovCellState, TimCellState } from "./hoverIdleState";
import {
  normalizeNHovBranchExperienceByBranch,
  normalizeNHovContinuousEffectProgressByBranch,
  normalizeNHovEffectBoundaryCaps,
} from "../nHov/nHovBranches";

export function cloneHovCells(
  cells: readonly HoverIdleHovCellState[],
): HovCellState[] {
  return cells.map((cell) => ({
    unlocked: cell.unlocked,
    hovGainMultiplier: cell.hovGainMultiplier,
    upgradeCost: cell.upgradeCost,
    purchasedUpgrades: cell.purchasedUpgrades,
  }));
}

export function cloneTimCells(
  cells: readonly HoverIdleTimCellState[],
): TimCellState[] {
  return cells.map((cell) => ({
    unlocked: cell.unlocked,
    timGainMultiplier: cell.timGainMultiplier,
    upgradeCost: cell.upgradeCost,
    purchasedUpgrades: cell.purchasedUpgrades,
  }));
}

export function cloneNHovState(state: NHovState): NHovState {
  const continuousEffectProgressByBranch =
    normalizeNHovContinuousEffectProgressByBranch(
      state.continuousEffectProgressByBranch,
    );
  return {
    count: state.count,
    branchExperienceByBranch: normalizeNHovBranchExperienceByBranch(
      state.branchExperienceByBranch,
    ),
    effectBoundaryCaps: normalizeNHovEffectBoundaryCaps(
      state.effectBoundaryCaps,
    ),
    ...(Object.keys(continuousEffectProgressByBranch).length === 0
      ? {}
      : { continuousEffectProgressByBranch }),
  };
}

export function cloneOHovState(state: OHovState): OHovState {
  return {
    count: state.count,
    isUnlocked: state.isUnlocked,
    power: state.power,
    hovCore: state.hovCore,
    records: {
      currentRunStartedAtMs: state.records.currentRunStartedAtMs,
      lastRunDurationMs: state.records.lastRunDurationMs,
      fastestRunDurationMs: state.records.fastestRunDurationMs,
      currentRunGameElapsedMs: state.records.currentRunGameElapsedMs,
      fastestRunGameDurationMs: state.records.fastestRunGameDurationMs,
    },
  };
}

export function clonePaymentState(
  state: HoverIdlePaymentState,
): HoverIdlePaymentState {
  return {
    token: state.token,
    redeemedRewardCodes: [...state.redeemedRewardCodes],
    purchasedPermanentItems: {
      addMousePointer: state.purchasedPermanentItems.addMousePointer,
      hovGainPercent: state.purchasedPermanentItems.hovGainPercent,
      timGainPercent: state.purchasedPermanentItems.timGainPercent,
    },
    entitlements: [...state.entitlements],
    claimedEntitlements: [...state.claimedEntitlements],
  };
}

export function cloneSettingsState(
  state: HoverIdleSettingsState,
): HoverIdleSettingsState {
  return {
    ...state,
    readHelpTopicIds: [...state.readHelpTopicIds],
  };
}

export function normalizePaymentState(
  state: Partial<HoverIdlePaymentState> | undefined,
): HoverIdlePaymentState {
  const items = state?.purchasedPermanentItems;
  return {
    token: state?.token ?? defaultPayment.token,
    redeemedRewardCodes: [...(state?.redeemedRewardCodes ?? [])],
    purchasedPermanentItems: {
      addMousePointer:
        items?.addMousePointer ??
        defaultPayment.purchasedPermanentItems.addMousePointer,
      hovGainPercent:
        items?.hovGainPercent ??
        defaultPayment.purchasedPermanentItems.hovGainPercent,
      timGainPercent:
        items?.timGainPercent ??
        defaultPayment.purchasedPermanentItems.timGainPercent,
    },
    entitlements: [...(state?.entitlements ?? [])],
    claimedEntitlements: [...(state?.claimedEntitlements ?? [])],
  };
}

export function withUpdatedPaymentState(
  current: HoverIdlePaymentState,
  overrides: {
    readonly token?: number;
    readonly redeemedRewardCodes?: HoverIdlePaymentState["redeemedRewardCodes"];
    readonly purchasedPermanentItems?: HoverIdlePaymentState["purchasedPermanentItems"];
    readonly entitlements?: HoverIdlePaymentState["entitlements"];
    readonly claimedEntitlements?: HoverIdlePaymentState["claimedEntitlements"];
  },
): HoverIdlePaymentState {
  const cloned = clonePaymentState(current);

  return {
    token: overrides.token ?? cloned.token,
    redeemedRewardCodes:
      overrides.redeemedRewardCodes !== undefined
        ? [...overrides.redeemedRewardCodes]
        : cloned.redeemedRewardCodes,
    purchasedPermanentItems:
      overrides.purchasedPermanentItems !== undefined
        ? { ...overrides.purchasedPermanentItems }
        : cloned.purchasedPermanentItems,
    entitlements:
      overrides.entitlements !== undefined
        ? [...overrides.entitlements]
        : cloned.entitlements,
    claimedEntitlements:
      overrides.claimedEntitlements !== undefined
        ? [...overrides.claimedEntitlements]
        : cloned.claimedEntitlements,
  };
}

export function areEntitlementSnapshotsEqual(
  left: EntitlementSnapshot,
  right: EntitlementSnapshot,
): boolean {
  return (
    left.scoreIncomeMultiplier === right.scoreIncomeMultiplier &&
    left.hoverIncomeMultiplier === right.hoverIncomeMultiplier &&
    left.timIncomeMultiplier === right.timIncomeMultiplier &&
    left.offlineProgressMultiplier === right.offlineProgressMultiplier &&
    left.hasSupporterPack === right.hasSupporterPack
  );
}
